import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { useLanguage } from '../context/LanguageContext';

const getOptimizedImageUrl = (url, width = 1600) => {
  if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url;
  return url.replace(
    'https://assets.auxodev.dk/',
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
};

export default function Page() {
  const { slug } = useParams();
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { language, t, getTranslatedContent } = useLanguage();

  // Determine the correct slug for fetching
  const currentSlug = slug || 'index';

  const translated = getTranslatedContent(currentSlug);
  const activeTitle = translated ? translated.title : (pageData ? pageData.title : '');
  const activeContent = translated ? translated.content : (pageData ? pageData.content : '');
  const activeMetaTitle = translated ? translated.meta_title : (pageData ? pageData.meta_title : '');
  const activeMetaDescription = translated ? translated.meta_description : (pageData ? pageData.meta_description : '');

  useEffect(() => {
    async function fetchPage() {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch(
          `https://api.dev.auxo.dk/public/v2/content/pages?page_slug=${currentSlug}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch page');
        }
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          setPageData(data.items[0]);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchPage();
  }, [currentSlug]);

  // Set metadata dynamically
  useEffect(() => {
    if (pageData || translated) {
      // Set Title
      const titleText = activeMetaTitle || `${activeTitle} - NNC Revision Ltd.`;
      document.title = titleText;

      // Set Meta Description
      const descText = activeMetaDescription || t('welcomeText2');
      let metaDescEl = document.querySelector('meta[name="description"]');
      if (!metaDescEl) {
        metaDescEl = document.createElement('meta');
        metaDescEl.setAttribute('name', 'description');
        document.head.appendChild(metaDescEl);
      }
      metaDescEl.setAttribute('content', descText);
    }
  }, [pageData, language, currentSlug, activeMetaTitle, activeTitle, activeMetaDescription, t]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-slate-200 rounded"></div>
          <div className="h-4 w-32 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || (!pageData && !translated)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <h1 className="text-4xl font-serif font-bold text-brand-navy mb-4">{t('pageNotFound')}</h1>
        <p className="text-slate-600 mb-8 max-w-md">
          {t('pageNotFoundDesc')}
        </p>
        <Link
          to="/"
          className="bg-brand-gold hover:bg-brand-gold-hover text-white font-semibold py-2.5 px-6 rounded text-sm transition-colors cursor-pointer uppercase tracking-wider"
        >
          {t('goToHome')}
        </Link>
      </div>
    );
  }

  const bannerUrl = pageData.banner_image && pageData.banner_image[0];

  return (
    <div className="pb-16">
      {/* Top Banner */}
      <div className="relative h-[250px] sm:h-[350px] md:h-[400px] w-full overflow-hidden bg-slate-900">
        {bannerUrl && (
          <img
            src={getOptimizedImageUrl(bannerUrl, 1600)}
            alt="Golden Gate Bridge Banner"
            className="w-full h-full object-cover object-center opacity-85"
          />
        )}
        {/* Dark overlay for calm, serious look */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/80 via-brand-navy/50 to-transparent" />
        
        {/* Banner Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              <span className="text-brand-gold font-semibold uppercase tracking-widest text-xs sm:text-sm block mb-2">
                NNC Revision Ltd.
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
                {activeTitle}
              </h1>
              {currentSlug === 'index' && (
                <p className="text-slate-200 text-sm sm:text-base mt-3 font-medium tracking-wide">
                  {t('tagline')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16">
        <div className="space-y-8">
          {currentSlug === 'index' ? (
              <div className="bg-white p-8 md:p-10 rounded-lg shadow-xs border border-slate-100">
                <div className="prose max-w-none">
                  <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-navy mb-6">
                    {t('welcomeTitle')}
                  </h2>
                  <div className="h-px bg-slate-200 w-20 mb-6" />
                  <p className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-4">
                    {t('newsCurrent')}
                  </p>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {t('welcomeText1')}
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    {t('welcomeText2')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white p-8 md:p-10 rounded-lg shadow-xs border border-slate-100">
                <div className="prose max-w-none">
                  
                  {activeContent ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: activeContent }} 
                      className="text-slate-600 leading-relaxed space-y-4 [&>h1]:text-2xl [&>h1]:font-serif [&>h1]:font-bold [&>h1]:text-brand-navy [&>h1]:mt-6 [&>h1]:mb-4 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:space-y-2 [&_a]:text-brand-gold [&_a]:hover:text-brand-navy [&_a]:underline [&_a]:transition-colors [&_a]:font-medium"
                    />
                  ) : (
                    <div className="text-slate-600 leading-relaxed space-y-4">
                      {language === 'da' ? (
                        <>
                          <p>
                            Vi tilbyder professionel rådgivning og skræddersyede løsninger inden for {activeTitle.toLowerCase()}. Vores mangeårige erfaring sikrer en tryg og effektiv proces, der tager højde for alle relevante juridiske og økonomiske aspekter.
                          </p>
                          <p>
                            Kontakt os for at høre mere om, hvordan vi kan hjælpe dig og din virksomhed med at opnå de bedste resultater.
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            We offer professional advice and tailored solutions within {activeTitle.toLowerCase()}. Our many years of experience ensure a safe and efficient process that takes into account all relevant legal and financial aspects.
                          </p>
                          <p>
                            Contact us to learn more about how we can help you and your business achieve the best results.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
